import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  LinearProgress,
  MenuItem,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add, HelpOutline, Print, Remove } from "@mui/icons-material";
import {
  WarehouseLabelPayload,
  WarehouseLabelPrintJob,
  WarehouseLabelPrinter,
  useWarehouseLabelPrint,
  useWarehouseLabelPrinters,
} from "hooks/useWarehouseLabel";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { LabelKind, MAX_LABELS, repeatedLabelCount, TOO_MANY_LABELS } from "utils/warehouseLabels";

// Off until a warehouse host can reach the VC-500Ws. Matches WarehouseLabels::PRINTING_FLAG.
const WAREHOUSE_LABEL_PRINTING = "warehouse_location_label_printing";

const MAX_COPIES = 20;

const CUT_MODES = [
  { value: "", label: "Automatic (cut one label, half-cut a batch)" },
  { value: "full", label: "Cut after every label" },
  { value: "strip", label: "Half-cut between labels, cut at the end" },
  { value: "half", label: "Half-cut after every label" },
  { value: "none", label: "Never cut" },
];

const STORAGE_KEY = "warehouseLabels.printer";

const remembered = (kind: LabelKind): string => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}")[kind] ?? "";
  } catch {
    return "";
  }
};

const remember = (kind: LabelKind, id: string) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}"), [kind]: id }),
    );
  } catch {
    // The choice still holds for this visit.
  }
};

const printerStatus = (printer: WarehouseLabelPrinter) =>
  printer.local ? "Ready · This computer" : "Warehouse network";

const helpTextFor = (printingEnabled: boolean, selected?: WarehouseLabelPrinter) => {
  if (!printingEnabled) return "Download the label as a PDF. Printing to a printer is turned off.";
  return selected?.local
    ? `Labels go to ${selected.name}, the printer installed on this computer. That connection cannot cut, so swipe the panel when it blinks. A warehouse VC-500W cuts automatically.`
    : `Labels are sent to ${selected ? selected.name : "the printer"} over the warehouse network. Cutting is automatic: one label is cut off, and a batch is half-cut into one strip.`;
};

// A warehouse VC-500W is told how to cut. The printer on this computer is an AirPrint queue and cannot be.
export function defaultPrinterId(printers: WarehouseLabelPrinter[], rememberedId: string): string {
  const saved = printers.find((printer) => printer.id === rememberedId);
  if (saved && !saved.local) return saved.id;
  const direct = printers.find((printer) => !printer.local);
  return direct?.id ?? saved?.id ?? printers[0]?.id ?? "";
}

// Copies repeat every code. One location becomes that many copies of its label. A batch stays
// intact when copies is 1, and each location in it is repeated when copies is higher.
export const withCopies = (payload: WarehouseLabelPayload, copies: number): WarehouseLabelPayload => {
  const codes = Array.isArray(payload.codes)
    ? payload.codes.filter((code): code is string => typeof code === "string")
    : [];
  if (codes.length === 0) return payload;
  const count = Math.min(MAX_COPIES, Math.max(1, Math.trunc(copies) || 1));
  return { ...payload, codes: codes.flatMap((code) => Array.from({ length: count }, () => code)) };
};

const labelCount = (payload: WarehouseLabelPayload | null) =>
  payload && Array.isArray(payload.codes) ? payload.codes.length : 0;

// A queued job has been accepted and is sitting until the warehouse print server picks it up.
// "Printing…" is only for a worker that has the printer.
const warehouseButtonLabel = (job: WarehouseLabelPrintJob | null, busy: boolean, count: number) => {
  if (job?.state === "queued" || (busy && !job)) return "Waiting for the print server…";
  if (busy) return "Printing…";
  return count > 1 ? "Print labels" : "Print label";
};

interface LabelPrintContextValue {
  kind: LabelKind;
  listError: string | null;
  printers: WarehouseLabelPrinter[];
  printerId: string;
  choose: (id: string) => void;
  selected?: WarehouseLabelPrinter;
  cutMode: string;
  setCutMode: (value: string) => void;
  copies: number;
  setCopies: React.Dispatch<React.SetStateAction<number>>;
  printLabel: () => void;
  canPrint: boolean;
  count: number;
  overLimit: boolean;
  printingEnabled: boolean;
  busy: boolean;
  job: WarehouseLabelPrintJob | null;
  error: string | null;
  helpText: string;
}

const LabelPrintContext = React.createContext<LabelPrintContextValue | null>(null);

const useLabelPrint = () => {
  const value = React.useContext(LabelPrintContext);
  if (!value) throw new Error("Label print controls must sit inside the print card.");
  return value;
};

interface LabelPrintProviderProps {
  kind: LabelKind;
  payload: WarehouseLabelPayload | null;
  children: React.ReactNode;
}

export const LabelPrintProvider: React.FC<LabelPrintProviderProps> = ({ kind, payload, children }) => {
  const { enabled: printingEnabled } = useFeatureFlag(WAREHOUSE_LABEL_PRINTING);
  const { printers: all, error: listError } = useWarehouseLabelPrinters(printingEnabled === true);
  const { print, job, error, busy } = useWarehouseLabelPrint();
  const printers = all.filter((printer) => printer.label_kind === kind);
  const [printerId, setPrinterId] = useState(() => remembered(kind));
  const [cutMode, setCutMode] = useState("");
  const [copies, setCopies] = useState(1);
  const selected = printers.find((printer) => printer.id === printerId);
  const appliedDefault = useRef(false);

  useEffect(() => {
    if (appliedDefault.current || printers.length === 0) return;
    appliedDefault.current = true;
    setPrinterId((current) => defaultPrinterId(printers, current));
  }, [printers]);

  const choose = useCallback(
    (id: string) => {
      appliedDefault.current = true;
      setPrinterId(id);
      remember(kind, id);
    },
    [kind],
  );

  const count = labelCount(payload);
  const overLimit = repeatedLabelCount(count, copies) > MAX_LABELS;

  const printLabel = useCallback(() => {
    if (!payload || !selected || busy || overLimit) return;
    print(withCopies(payload, copies), selected.id, kind === "location" && !selected.local ? cutMode : "");
  }, [payload, selected, busy, overLimit, print, copies, kind, cutMode]);

  const helpText = useMemo(() => helpTextFor(printingEnabled === true, selected), [printingEnabled, selected]);
  const canPrint = Boolean(payload && selected && !busy && !overLimit);

  const value = useMemo(
    () => ({
      kind,
      listError,
      printers,
      printerId: selected ? printerId : "",
      choose,
      selected,
      cutMode,
      setCutMode,
      copies,
      setCopies,
      printLabel,
      canPrint,
      count,
      overLimit,
      printingEnabled: printingEnabled === true,
      busy,
      job,
      error,
      helpText,
    }),
    [
      kind,
      listError,
      printers,
      printerId,
      selected,
      choose,
      cutMode,
      copies,
      printLabel,
      canPrint,
      count,
      overLimit,
      printingEnabled,
      busy,
      job,
      error,
      helpText,
    ],
  );

  return <LabelPrintContext.Provider value={value}>{children}</LabelPrintContext.Provider>;
};

const PrinterValue = ({ printer }: { printer: WarehouseLabelPrinter }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.25, textAlign: "left" }}>
    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "success.main", flexShrink: 0 }} />
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" noWrap>
        {printer.name}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" noWrap>
        {printerStatus(printer)}
      </Typography>
    </Box>
  </Stack>
);

export const LabelPrintHeader: React.FC = () => {
  const { helpText, printingEnabled } = useLabelPrint();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
      <Box>
        <Typography variant="overline" color="primary" display="block" lineHeight={1.4}>
          {printingEnabled ? "Label printing" : "Label"}
        </Typography>
        <Typography variant="h5" component="h2">
          {printingEnabled ? "Preview and print" : "Preview"}
        </Typography>
      </Box>
      <Button
        type="button"
        color="primary"
        startIcon={<HelpOutline />}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{ flexShrink: 0 }}
      >
        {printingEnabled ? "Printer help" : "Label help"}
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Typography variant="body2" sx={{ p: 2, maxWidth: 320 }}>
          {helpText}
        </Typography>
      </Popover>
    </Stack>
  );
};

export const LabelPrintControls: React.FC<{
  children?: React.ReactNode | ((copies: number, overLimit: boolean) => React.ReactNode);
}> = ({ children }) => {
  const {
    kind,
    listError,
    printers,
    printerId,
    choose,
    selected,
    cutMode,
    setCutMode,
    copies,
    setCopies,
    printLabel,
    canPrint,
    count,
    overLimit,
    printingEnabled,
    busy,
    job,
  } = useLabelPrint();
  const showCutting = kind === "location" && !selected?.local;

  return (
    <Stack spacing={2}>
      {printingEnabled && listError && <Alert severity="error">{listError}</Alert>}
      {printingEnabled && (
        <>
          <TextField
            select
            fullWidth
            label="Printer"
            value={printerId}
            onChange={(event) => choose(event.target.value)}
            disabled={busy}
            SelectProps={{
              renderValue: (value) => {
                const printer = printers.find((item) => item.id === value);
                return printer ? <PrinterValue printer={printer} /> : "";
              },
            }}
            sx={{ "& .MuiSelect-select": { display: "flex", alignItems: "center", whiteSpace: "normal" } }}
          >
            {printers.map((printer) => (
              <MenuItem key={printer.id} value={printer.id}>
                <PrinterValue printer={printer} />
              </MenuItem>
            ))}
          </TextField>
          {showCutting && (
            <TextField
              select
              fullWidth
              label="Cutting"
              value={cutMode}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              onChange={(event) => setCutMode(event.target.value)}
              disabled={busy}
            >
              {CUT_MODES.map((mode) => (
                <MenuItem key={mode.value} value={mode.value}>
                  {mode.label}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="overline" color="text.secondary">
              Copies
            </Typography>
            <Stack direction="row" alignItems="center" sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
              <IconButton
                type="button"
                aria-label="Fewer copies"
                size="small"
                disabled={busy || copies <= 1}
                onClick={() => setCopies((current) => Math.max(1, current - 1))}
              >
                <Remove />
              </IconButton>
              <Typography variant="body2" sx={{ minWidth: 24, textAlign: "center" }}>
                {copies}
              </Typography>
              <IconButton
                type="button"
                aria-label="More copies"
                size="small"
                disabled={busy || copies >= MAX_COPIES}
                onClick={() => setCopies((current) => Math.min(MAX_COPIES, current + 1))}
              >
                <Add />
              </IconButton>
            </Stack>
          </Stack>
          <Button
            type="button"
            variant="contained"
            fullWidth
            startIcon={<Print />}
            disabled={!canPrint}
            onClick={printLabel}
          >
            {warehouseButtonLabel(job, busy, count)}
          </Button>
        </>
      )}
      {overLimit && <Alert severity="warning">{TOO_MANY_LABELS}</Alert>}
      {typeof children === "function" ? children(copies, overLimit) : children}
    </Stack>
  );
};

export const LabelPrintStatus: React.FC = () => {
  const { job, error } = useLabelPrint();
  const [dismissedId, setDismissedId] = useState<string | null>(null);
  const progress = job && job.total > 0 ? (job.done / job.total) * 100 : undefined;
  const running = job && job.state !== "done" && job.state !== "error";
  const succeeded = job?.state === "done" && dismissedId !== job.id;

  if (!error && !running && !succeeded && job?.state !== "error") return null;

  return (
    <Stack spacing={1} sx={{ mt: 2 }}>
      {error && job?.state !== "error" && <Alert severity="error">{error}</Alert>}
      {running && (
        <Stack spacing={1}>
          <LinearProgress variant={progress === undefined ? "indeterminate" : "determinate"} value={progress} />
          <Typography variant="body2" color="text.secondary">
            {job.detail}
            {job.total > 0 && ` (${job.done} of ${job.total})`}
          </Typography>
        </Stack>
      )}
      {job?.state === "error" && (
        <Alert severity="error">
          {error || job.error}
          {job.done > 0 && ` ${job.done} of ${job.total} already printed.`}
        </Alert>
      )}
      {succeeded && (
        <Alert severity="success" onClose={() => setDismissedId(job.id)}>
          <Typography variant="body2" fontWeight={700}>
            Printed successfully
          </Typography>
          <Typography variant="body2">{job.printer} · just now</Typography>
        </Alert>
      )}
    </Stack>
  );
};
